<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { DateTime } from 'luxon'
import { isUndefined } from 'lodash'

import ViewLayout from '@/views/layouts/ViewLayout.vue'
import RcCustomerSelect from '@/components/rc/common/RcCustomerSelect.vue'
import RcSiteSelect from '@/components/rc/common/RcSiteSelect.vue'
import type { ICustomer } from '@/types/core/Customer'
import type { ISiteIdentifier } from '@/components/rc/common/RcSiteSelect.vue'

import ProgramSuccessBarChart from '@/components/cpm/charts/ProgramSuccessBarChart.vue'
import ProgramSuccessCurtailmentsList from '@/components/cpm/ProgramSuccessCurtailmentsList.vue'
import ProgramSuccessMetrics from '@/components/cpm/ProgramSuccessMetrics.vue'
import ProgramSuccessSiteInfo from '@/components/cpm/ProgramSuccessSiteInfo.vue'
import { useProgramSuccess } from '@/composables/cpm/programSuccess'
import type { IProgramSuccessFilters, ProgramSuccessInterval } from '@/types/cpm/programSuccess'

const customerSelected = ref<ICustomer>()
const siteSelected = ref<ISiteIdentifier>()
const intervalSelected = ref<ProgramSuccessInterval>('last_12_months')

// Month/year the curtailment list is scoped to. Lifted here so a click on the
// bar chart can drill the list into the selected month. Defaults to now.
const now = DateTime.now()
const monthSelected = ref<number>(now.month) // 1-12
const yearSelected = ref<number>(now.year)

function drillIntoMonth({ year, month }: { year: number; month: number }) {
  yearSelected.value = year
  monthSelected.value = month
}

// Reset the site whenever the customer changes so a stale site can't linger
// scoped to a different customer.
watch(customerSelected, () => {
  siteSelected.value = undefined
})

const filters = computed<IProgramSuccessFilters>(() => ({
  site_id: siteSelected.value?.site_id,
  customer_id: customerSelected.value?.id,
  interval: intervalSelected.value,
}))

const { getProgramSuccessQuery } = useProgramSuccess()
const { data, error, isLoading, isFetching } = getProgramSuccessQuery(filters)

const hasSite = computed(() => !isUndefined(siteSelected.value?.site_id))
const displayTimezone = computed(() => siteSelected.value?.timezone ?? 'UTC')

function refetchFailed() {
  siteSelected.value = undefined
}
</script>

<template>
  <!-- h-screen falls through to ViewLayout's root, giving it a definite height so its flex-1 <main> fills -->
  <ViewLayout class="h-screen">
    <!-- Customer picker scopes the site picker; both live in the header controls slot -->
    <template #controls>
      <rc-customer-select v-model="customerSelected" class="w-75" placeholder="Customer" :disabled="isFetching" />
      <rc-site-select
        v-model="siteSelected"
        class="w-75"
        placeholder="Site"
        :disabled="isFetching || !customerSelected"
        :customer-id="customerSelected?.id"
      />
    </template>

    <!-- Error state -->
    <rl-error-result v-if="error && !isFetching" :error="error">
      <el-button class="mt-4" @click="refetchFailed()">
        <Icon icon="material-symbols:refresh-rounded" height="16" width="16" :inline="true" />&nbsp;Reload
      </el-button>
    </rl-error-result>

    <template v-else>
      <!-- Empty: no site selected -->
      <div v-if="!hasSite" class="flex-1 min-h-0 flex items-center justify-center">
        <span class="text-gray-500">
          {{
            customerSelected
              ? 'Please select a site to view program success.'
              : 'Please select a customer and site to view program success.'
          }}
        </span>
      </div>

      <!-- Staggered two-column card grid (each column sums to 12fr) -->
      <div v-else class="flex-1 min-h-0 flex gap-4">
        <!-- Left column: chart (5fr) over curtailments list (7fr) -->
        <div class="w-1/2 flex flex-col gap-4 min-h-0">
          <!-- Barchart card -->
          <el-card class="flex-[5] min-h-0">
            <ProgramSuccessBarChart
              v-model:interval="intervalSelected"
              :months="data?.months"
              :loading="isLoading"
              @select-month="drillIntoMonth"
            />
          </el-card>

          <el-card class="flex-[7] min-h-0">
            <ProgramSuccessCurtailmentsList
              v-model:month="monthSelected"
              v-model:year="yearSelected"
              :site-id="siteSelected?.site_id"
              :timezone="displayTimezone"
            />
          </el-card>
        </div>

        <!-- Right column -->
        <div class="w-1/2 flex flex-col gap-4 min-h-0">
          <!-- Program success metrics -->
          <el-card class="flex-[4] min-h-0">
            <ProgramSuccessMetrics :data="data" :loading="isLoading" />
          </el-card>

          <!-- Site information -->
          <el-card class="flex-[8] min-h-0">
            <ProgramSuccessSiteInfo :site-id="siteSelected?.site_id" :agreement="data?.agreement ?? null" />
          </el-card>
        </div>
      </div>
    </template>
  </ViewLayout>
</template>

<style scoped lang="scss">
:deep() {
  .el-card {
    &__body {
      height: 100%;
      box-sizing: border-box;
      padding: 24px;
    }
  }
}
</style>
